'use client';
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { DotPerson } from '@/data/dotsData';

export interface GridCamera {
  dotId: string;
  type: 'A' | 'B';
  dot: DotPerson;
}

export const GRID_COLS = 4;
export const GRID_ROWS = 6;
export const GRID_TOTAL = GRID_COLS * GRID_ROWS; // 24

interface GridContextValue {
  gridCells: Array<GridCamera | null>;
  gridDotIds: Set<string>; // dotIds (not dot.id) currently in the grid
  addDotToGrid: (dot: DotPerson, type?: 'A' | 'B') => void;
  removeDotFromGrid: (idx: number) => void;
  clearAllCells: () => void;
  selectedGridIndices: Set<number>;
  toggleGridSelection: (idx: number, multi: boolean) => void;
  clearGridSelection: () => void;
}

const GridContext = createContext<GridContextValue | null>(null);

export function GridProvider({ children }: { children: React.ReactNode }) {
  // Default: empty grid
  const [gridCells, setGridCells] = useState<Array<GridCamera | null>>(
    () => Array(GRID_TOTAL).fill(null)
  );
  const [selectedGridIndices, setSelectedGridIndices] = useState<Set<number>>(new Set());

  const gridDotIds = React.useMemo(() => {
    const ids = new Set<string>();
    gridCells.forEach(cell => { if (cell) ids.add(cell.dotId); });
    return ids;
  }, [gridCells]);

  const addDotToGrid = useCallback((dot: DotPerson, type: 'A' | 'B' = 'A') => {
    setGridCells(prev => {
      // Find first empty slot
      const emptyIdx = prev.findIndex(c => c === null);
      if (emptyIdx === -1) return prev; // grid full
      const next = [...prev];
      next[emptyIdx] = { dotId: dot.dotId, type, dot };
      return next;
    });
  }, []);

  const removeDotFromGrid = useCallback((idx: number) => {
    setGridCells(prev => {
      const next = [...prev];
      next[idx] = null;
      return next;
    });
    setSelectedGridIndices(prev => {
      const next = new Set(prev);
      next.delete(idx);
      return next;
    });
  }, []);

  const clearAllCells = useCallback(() => {
    setGridCells(Array(GRID_TOTAL).fill(null));
    setSelectedGridIndices(new Set());
  }, []);

  const toggleGridSelection = useCallback((idx: number, multi: boolean) => {
    setSelectedGridIndices(prev => {
      if (multi) {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        return next;
      } else {
        if (prev.size === 1 && prev.has(idx)) return new Set();
        return new Set([idx]);
      }
    });
  }, []);

  const clearGridSelection = useCallback(() => {
    setSelectedGridIndices(new Set());
  }, []);

  return (
    <GridContext.Provider value={{
      gridCells,
      gridDotIds,
      addDotToGrid,
      removeDotFromGrid,
      clearAllCells,
      selectedGridIndices,
      toggleGridSelection,
      clearGridSelection,
    }}>
      {children}
    </GridContext.Provider>
  );
}

export function useGrid() {
  const ctx = useContext(GridContext);
  if (!ctx) throw new Error('useGrid must be used within GridProvider');
  return ctx;
}
